import {useCallback, useReducer} from 'react';

import {
  AgentApprovalCategory,
  EventTriggerConfig,
  FileMessageBlock,
  TimeTriggerConfig,
  TriggerType,
  withCanonicalObjectScopes,
} from './RoutineTypes';
import {DEFAULT_RUN_TIME_STRING} from './RoutineUtils';
import {FIELD_CHANGED, OBJECT_CREATED} from './triggerConstants';

// Deriving the access state from the two stored primitives (rather than storing a card enum) makes
// the rejected combination (WRITE_ACTIONS + folder) unrepresentable. An empty-string folder id marks
// "Full access limited to a folder, folder not yet picked"; save is blocked while it is empty so it
// never reaches the backend.
export type AccessLevel = 'default' | 'full' | 'targeted';

export function deriveAccessLevel(
  allowedApprovalCategories: Array<AgentApprovalCategory>,
  agentWorkspaceFolderId: string | null
): AccessLevel {
  if (allowedApprovalCategories.includes(AgentApprovalCategory.WRITE_ACTIONS)) {
    return 'full';
  }
  if (agentWorkspaceFolderId != null) {
    return 'targeted';
  }
  return 'default';
}

export interface RoutineFormState {
  name: string;
  description: string;
  prompt: string;
  fileBlocks: Array<FileMessageBlock>;
  triggerType: TriggerType | null;
  triggerConfig: Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>;
  allowedApprovalCategories: Array<AgentApprovalCategory>;
  agentWorkspaceFolderId: string | null;
  memoryEnabled: boolean;
}

interface ReducerState {
  form: RoutineFormState;
  baseline: RoutineFormState;
  stashedTimeTriggerConfig: Partial<TimeTriggerConfig>;
  stashedEventTriggerConfig: Partial<EventTriggerConfig>;
}

type RoutineFormAction =
  | {type: 'SET_NAME'; value: string}
  | {type: 'SET_DESCRIPTION'; value: string}
  | {type: 'SET_PROMPT'; value: string}
  | {type: 'ADD_FILE_BLOCK'; fileBlock: FileMessageBlock}
  | {type: 'REMOVE_FILE_BLOCK'; index: number}
  | {type: 'SET_TRIGGER_TYPE'; value: TriggerType}
  | {type: 'UPDATE_TRIGGER_CONFIG'; updates: Partial<TimeTriggerConfig | EventTriggerConfig>}
  | {type: 'SET_ACCESS_LEVEL'; value: AccessLevel}
  | {type: 'SET_AGENT_WORKSPACE_FOLDER'; value: string | null}
  | {type: 'SET_MEMORY_ENABLED'; value: boolean}
  | {type: 'RESET'; state: RoutineFormState};

function normalizeEventTriggerConfig(
  currentConfig: Partial<EventTriggerConfig>,
  updates: Partial<EventTriggerConfig>
): Partial<EventTriggerConfig> {
  const nextConfig: Partial<EventTriggerConfig> = {...currentConfig, ...updates};

  // A user is either setting up a trigger for specific objects or all objects of a schema
  // so these are mutually exclusive.
  if (updates.schema_id != null) {
    nextConfig.object_ids = undefined;
  }
  if (updates.object_ids != null) {
    nextConfig.schema_id = undefined;
  }

  // A routine is triggered by either an object being created
  // or a field changing to some value, not both.
  if (nextConfig.trigger_kind === OBJECT_CREATED) {
    nextConfig.field = undefined;
    nextConfig.to_values = undefined;
  } else if (nextConfig.trigger_kind === FIELD_CHANGED) {
    nextConfig.field = nextConfig.field ?? '';
    nextConfig.to_values = nextConfig.to_values ?? [];
  } else {
    nextConfig.field = undefined;
    nextConfig.to_values = undefined;
  }

  // Remove undefined keys so JSON.stringify-based dirty checks do not treat
  // `{field: undefined}` and `{}` as different trigger configs.
  if (nextConfig.field == null) {
    delete nextConfig.field;
  }
  if (nextConfig.to_values == null) {
    delete nextConfig.to_values;
  }
  if (nextConfig.schema_id == null) {
    delete nextConfig.schema_id;
  }
  if (nextConfig.object_ids == null) {
    delete nextConfig.object_ids;
  }
  // An empty (or absent) selection omits object_scopes so the backend applies its default
  // ("viewable"), and dirty checks don't treat `{object_scopes: []}` as distinct from `{}`.
  if (nextConfig.object_scopes == null || nextConfig.object_scopes.length === 0) {
    delete nextConfig.object_scopes;
  }

  return nextConfig;
}

function formReducer(state: ReducerState, action: RoutineFormAction): ReducerState {
  switch (action.type) {
    case 'SET_NAME':
      return {...state, form: {...state.form, name: action.value}};
    case 'SET_DESCRIPTION':
      return {...state, form: {...state.form, description: action.value}};
    case 'SET_PROMPT':
      return {...state, form: {...state.form, prompt: action.value}};
    case 'ADD_FILE_BLOCK':
      return {
        ...state,
        form: {...state.form, fileBlocks: [...state.form.fileBlocks, action.fileBlock]},
      };
    case 'REMOVE_FILE_BLOCK':
      return {
        ...state,
        form: {...state.form, fileBlocks: state.form.fileBlocks.filter((_, i) => i !== action.index)},
      };
    case 'SET_TRIGGER_TYPE': {
      if (action.value === state.form.triggerType) {
        return state;
      }
      const isCurrentlyTimeBased = state.form.triggerType === TriggerType.TIME_BASED;
      const stashedTimeTriggerConfig = isCurrentlyTimeBased
        ? (state.form.triggerConfig as Partial<TimeTriggerConfig>)
        : state.stashedTimeTriggerConfig;
      const stashedEventTriggerConfig =
        !isCurrentlyTimeBased && state.form.triggerType != null
          ? (state.form.triggerConfig as Partial<EventTriggerConfig>)
          : state.stashedEventTriggerConfig;
      const restoredConfig =
        action.value === TriggerType.TIME_BASED ? stashedTimeTriggerConfig : stashedEventTriggerConfig;
      return {
        ...state,
        stashedTimeTriggerConfig,
        stashedEventTriggerConfig,
        form: {
          ...state.form,
          triggerType: action.value,
          triggerConfig: restoredConfig,
        },
      };
    }
    case 'UPDATE_TRIGGER_CONFIG':
      if (state.form.triggerType === TriggerType.EVENT_BASED) {
        return {
          ...state,
          form: {
            ...state.form,
            triggerConfig: normalizeEventTriggerConfig(
              state.form.triggerConfig as Partial<EventTriggerConfig>,
              action.updates as Partial<EventTriggerConfig>
            ),
          },
        };
      }
      return {
        ...state,
        form: {...state.form, triggerConfig: {...state.form.triggerConfig, ...action.updates}},
      };
    case 'SET_ACCESS_LEVEL': {
      // Map the card back to primitives so the rejected combination (WRITE_ACTIONS + folder) is never stored.
      if (action.value === 'full') {
        return {
          ...state,
          form: {
            ...state.form,
            allowedApprovalCategories: [AgentApprovalCategory.WRITE_ACTIONS],
            agentWorkspaceFolderId: null,
          },
        };
      }
      if (action.value === 'targeted') {
        // Keep any previously-chosen folder; otherwise mark the folder as pending with ''.
        return {
          ...state,
          form: {
            ...state.form,
            allowedApprovalCategories: [],
            agentWorkspaceFolderId: state.form.agentWorkspaceFolderId ?? '',
          },
        };
      }
      return {
        ...state,
        form: {...state.form, allowedApprovalCategories: [], agentWorkspaceFolderId: null},
      };
    }
    case 'SET_AGENT_WORKSPACE_FOLDER':
      return {
        ...state,
        form: {...state.form, agentWorkspaceFolderId: action.value},
      };
    case 'SET_MEMORY_ENABLED':
      return {
        ...state,
        form: {...state.form, memoryEnabled: action.value},
      };
    case 'RESET':
      return {
        form: action.state,
        baseline: action.state,
        stashedTimeTriggerConfig: getDefaultTimeTriggerConfig(),
        stashedEventTriggerConfig: getDefaultEventTriggerConfig(),
      };
    default:
      return state;
  }
}

export function getDefaultTimeTriggerConfig(): Partial<TimeTriggerConfig> {
  return {
    timezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    run_time: DEFAULT_RUN_TIME_STRING,
    freq_type: 'weekly',
    recurrence_interval: 1,
    weekly_days: [],
  };
}

export function getDefaultEventTriggerConfig(): Partial<EventTriggerConfig> {
  return {
    object_type: '',
    trigger_kind: undefined,
  };
}

// TODO(nweeden): Consider replacing reducer + shallowFormEqual with immer + react-fast-compare
export function shallowFormEqual(a: RoutineFormState, b: RoutineFormState): boolean {
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.prompt === b.prompt &&
    a.triggerType === b.triggerType &&
    a.agentWorkspaceFolderId === b.agentWorkspaceFolderId &&
    a.memoryEnabled === b.memoryEnabled &&
    JSON.stringify(a.allowedApprovalCategories) === JSON.stringify(b.allowedApprovalCategories) &&
    // object_scopes is an order-agnostic union, so compare in canonical order to avoid a reordered
    // selection reading as dirty.
    JSON.stringify(withCanonicalObjectScopes(a.triggerConfig)) ===
      JSON.stringify(withCanonicalObjectScopes(b.triggerConfig)) &&
    JSON.stringify(a.fileBlocks) === JSON.stringify(b.fileBlocks)
  );
}

interface UseRoutineFormResult {
  formState: RoutineFormState;
  dispatch: React.Dispatch<RoutineFormAction>;
  isDirty: boolean;
  reset: (newState?: RoutineFormState) => void;
}

export default function useRoutineForm(initialState: RoutineFormState): UseRoutineFormResult {
  const [state, dispatch] = useReducer(formReducer, {
    form: initialState,
    baseline: initialState,
    stashedTimeTriggerConfig: getDefaultTimeTriggerConfig(),
    stashedEventTriggerConfig: getDefaultEventTriggerConfig(),
  });

  const isDirty = !shallowFormEqual(state.form, state.baseline);

  const reset = useCallback(
    (newState?: RoutineFormState) => {
      dispatch({type: 'RESET', state: newState ?? initialState});
    },
    [initialState]
  );

  return {formState: state.form, dispatch, isDirty, reset};
}
